import { useEffect, useState, type FormEvent } from 'react';
import { useApi, useSession } from '@adiox/ui';
import { useActiveLogin } from '../trading/useTrading';

const field =
  'w-full rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent';
const label = 'mb-1 block text-xs uppercase tracking-wide text-muted';
const CONFIRM = 'X-Confirm-Password';

/**
 * The investor (read-only) password of the active account: what a prop client gives a verification site or a mentor so they
 * can watch the account without being able to trade it. Setting or removing it asks for the account owner's own password again.
 */
export function InvestorPasswordSection() {
  const api = useApi();
  const login = useActiveLogin();
  const readOnly = useSession().user?.readOnly === true;
  const [state, setState] = useState<{ enabled: boolean; setAt?: string | null } | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [own, setOwn] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (login === undefined || readOnly) return;
    let cancelled = false;
    void api
      .GET('/api/v1/accounts/{login}/investor-password', { params: { path: { login } } })
      .then(({ data }) => !cancelled && data && setState({ enabled: data.enabled, setAt: data.setAt }));
    return () => {
      cancelled = true;
    };
  }, [api, login, readOnly]);

  if (login === undefined || readOnly) return null;

  const fail = (status: number, body: unknown, fallback: string) => {
    const problem = (body ?? {}) as { detail?: string; title?: string };
    setMessage({
      tone: 'error',
      text:
        status === 403 && problem.title === 'Confirmation required'
          ? 'Your own password is not correct.'
          : status === 429
            ? 'Too many attempts. Wait a minute and try again.'
            : (problem.detail ?? fallback),
    });
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirm)
      return setMessage({ tone: 'error', text: 'The two passwords are not the same.' });
    if (password.length < 12) return setMessage({ tone: 'error', text: 'Use at least 12 characters.' });
    if (!own) return setMessage({ tone: 'error', text: 'Enter your own password to confirm.' });
    setBusy(true);
    setMessage(null);
    const { data, error, response } = await api.PUT('/api/v1/accounts/{login}/investor-password', {
      params: { path: { login } },
      body: { password },
      headers: { [CONFIRM]: own },
    });
    setBusy(false);
    if (response.ok && data) {
      setState({ enabled: data.enabled, setAt: data.setAt });
      setPassword('');
      setConfirm('');
      setOwn('');
      setMessage({
        tone: 'ok',
        text: 'Saved. Anyone signed in with the old investor password was signed out.',
      });
      return;
    }
    fail(response.status, error, 'The investor password could not be saved.');
  };

  const remove = async () => {
    if (!own) return setMessage({ tone: 'error', text: 'Enter your own password to confirm.' });
    setBusy(true);
    setMessage(null);
    const { data, error, response } = await api.DELETE('/api/v1/accounts/{login}/investor-password', {
      params: { path: { login } },
      headers: { [CONFIRM]: own },
    });
    setBusy(false);
    if (response.ok && data) {
      setState({ enabled: false });
      setOwn('');
      setMessage({ tone: 'ok', text: 'Removed. Investor sessions were signed out.' });
      return;
    }
    fail(response.status, error, 'The investor password could not be removed.');
  };

  return (
    <form className="flex flex-col gap-2" noValidate onSubmit={save} aria-label="Investor password">
      <h3 className="text-sm font-semibold">Investor (read-only) password</h3>
      <p className="text-xs text-muted">
        Share this with a mentor or a verification site: they sign in with account {login} and this password,
        and can look but not trade or change anything.{' '}
        {state?.enabled ? 'One is set for this account.' : 'None is set for this account.'}
      </p>
      <div>
        <label className={label} htmlFor="inv-new">
          New investor password
        </label>
        <input
          id="inv-new"
          type="password"
          autoComplete="new-password"
          className={field}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <div>
        <label className={label} htmlFor="inv-repeat">
          Repeat it
        </label>
        <input
          id="inv-repeat"
          type="password"
          autoComplete="new-password"
          className={field}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </div>
      <div>
        <label className={label} htmlFor="inv-own">
          Your own password (to confirm)
        </label>
        <input
          id="inv-own"
          type="password"
          autoComplete="current-password"
          className={field}
          value={own}
          onChange={(e) => setOwn(e.target.value)}
        />
      </div>
      <p
        role="alert"
        className={`min-h-[1.25rem] text-sm ${message?.tone === 'ok' ? 'text-up' : 'text-down'}`}
      >
        {message?.text}
      </p>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-accent px-3 py-1 text-sm font-semibold text-accent-fg disabled:opacity-40"
        >
          {state?.enabled ? 'Replace investor password' : 'Set investor password'}
        </button>
        {state?.enabled && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void remove()}
            className="rounded-md border border-line px-3 py-1 text-sm hover:border-accent disabled:opacity-40"
          >
            Remove
          </button>
        )}
      </div>
    </form>
  );
}
